import React from 'react';
import { X, Globe, Eye } from 'lucide-react';
import type { ForecastVersion } from '../../types';

interface VersionSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetVersion: ForecastVersion | null;
  onSetDefault: () => void;
  onViewOnly: () => void;
  isLoading?: boolean;
}

export function VersionSwitchModal({
  isOpen,
  onClose,
  targetVersion,
  onSetDefault,
  onViewOnly,
  isLoading = false,
}: VersionSwitchModalProps) {
  if (!isOpen || !targetVersion) return null;

  return (
    <div className="fv3-root fv3-scrim fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: 'rgba(15,15,17,.5)', backdropFilter: 'blur(2px)' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fv3-dialog relative rounded-xl max-w-md w-full mx-4 overflow-hidden" style={{ background: 'var(--lodge-white)', boxShadow: 'var(--shadow-xl)' }}>
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
          <h2 className="fv3-dialog-title text-lg font-semibold" style={{ color: 'var(--lodge-onyx-800)', fontFamily: 'var(--font-sans)', letterSpacing: '-0.2px' }}>
            Switch Version
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded-lg transition-colors disabled:opacity-50"
            style={{ color: 'var(--lodge-onyx-400)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-700)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="mb-6" style={{ color: 'var(--lodge-onyx-600)' }}>
            You're switching to <span className="font-semibold" style={{ color: 'var(--lodge-onyx-800)' }}>"{targetVersion.name}"</span>.
            How would you like to proceed?
          </p>

          {/* Options */}
          <div className="space-y-3">
            {/* Set as Default Option */}
            <button
              onClick={onSetDefault}
              disabled={isLoading}
              className="w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--lodge-onyx-100)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--lodge-orange-400)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-50)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--lodge-onyx-100)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <div className="p-2 rounded-lg" style={{ background: 'var(--lodge-orange-100)', color: 'var(--lodge-orange-600)' }}>
                <Globe size={20} />
              </div>
              <div className="flex-1">
                <div className="font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                  Set as default for all users
                </div>
                <div className="text-sm mt-0.5" style={{ color: 'var(--lodge-onyx-500)' }}>
                  This version will be shown to everyone who opens this location's forecast.
                </div>
              </div>
            </button>

            {/* View Only Option */}
            <button
              onClick={onViewOnly}
              disabled={isLoading}
              className="w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'var(--lodge-onyx-100)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--lodge-onyx-300)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-onyx-50)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--lodge-onyx-100)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <div className="p-2 rounded-lg" style={{ background: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-600)' }}>
                <Eye size={20} />
              </div>
              <div className="flex-1">
                <div className="font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                  View for this session only
                </div>
                <div className="text-sm mt-0.5" style={{ color: 'var(--lodge-onyx-500)' }}>
                  Switch to this version temporarily. Other users won't be affected.
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot px-6 py-4" style={{ background: 'var(--lodge-onyx-50)', borderTop: '1px solid var(--lodge-onyx-100)' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-full px-4 py-2 text-sm transition-colors disabled:opacity-50"
            style={{ color: 'var(--lodge-onyx-600)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--lodge-onyx-900)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--lodge-onyx-600)')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default VersionSwitchModal;
